import { useEffect, useMemo, useRef } from 'react';
import { UseFieldArrayReturn, UseFormReturn, useWatch } from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';

import {
  HANDLING_UNIT_TYPES_THREEG,
  PACKAGE_TYPE_TO_CODE,
  QuickQuoteInputs,
} from '../../types';
import { LTLTrailerVisualizationBlock } from '../LTLTrailerVisualizationBlock';
import {
  buildItemColorMap,
  useStableItemIds,
} from '../components/visualizationColors';
import { useIs3DVisualisationEnabled } from '../hooks/useLTLQuickQuoteConfigFlags';
import { ItemCard } from './ItemCard';
import { useThreeGCommodities } from './hooks/useThreeGCommodities';

interface ItemsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  itemFieldArray: Pick<
    UseFieldArrayReturn<QuickQuoteInputs, 'ltl.items'>,
    'fields' | 'append' | 'remove' | 'replace'
  >;
}

// ThreeG LTL items section; item cards emphasize dimensions and per-item data (no package type).
export function ItemsSection({
  formMethods,
  itemFieldArray,
}: ItemsSectionProps) {
  // Default to Pallet ("3") for ThreeG; keep fallback in case options change.
  const defaultHandlingUnitType =
    HANDLING_UNIT_TYPES_THREEG.find((opt) => opt.value === '3')?.value ?? '3';
  const {
    control,
    formState: { errors },
  } = formMethods;

  const {
    commodities,
    isLoading: isLoadingCommodities,
    hasCustomer,
    hasCustomerId,
  } = useThreeGCommodities();

  const { fields, append, remove, replace } = itemFieldArray;
  const items = useWatch({ control, name: 'ltl.items' }) ?? [];
  const is3DVisualisationEnabled = useIs3DVisualisationEnabled();
  const itemIds = useStableItemIds(items);
  const itemColorMap = useMemo(() => buildItemColorMap(itemIds), [itemIds]);

  const hasInitializedRef = useRef(false);

  const itemsError = errors.ltl?.items;
  const arrayLevelError =
    itemsError && 'root' in itemsError && itemsError.root?.message
      ? itemsError.root.message
      : itemsError && 'message' in itemsError
        ? (itemsError.message as string)
        : null;

  useEffect(() => {
    if (fields.length === 0) {
      hasInitializedRef.current = false;
    }
  }, [fields.length]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    if (fields.length === 0) {
      append({
        itemId: crypto.randomUUID(),
        description: '',
        freightClassLocked: false,
        length: undefined,
        width: undefined,
        height: undefined,
        weight: undefined,
        handlingUnits: undefined,
        handlingUnitType: defaultHandlingUnitType,
        pieces: undefined,
        packageType: PACKAGE_TYPE_TO_CODE['Pallet'],
      });
    }
  }, []);

  const handleRemoveItem = (index: number) => {
    remove(index);
  };

  const handleAddItem = () => {
    const item = {
      itemId: crypto.randomUUID(),
      description: '',
      freightClassLocked: false,
      length: undefined,
      width: undefined,
      height: undefined,
      weight: undefined,
      handlingUnits: undefined,
      handlingUnitType: defaultHandlingUnitType,
      pieces: undefined,
      packageType: PACKAGE_TYPE_TO_CODE['Pallet'],
    };

    if (fields.length === 0) {
      replace([item]);
      return;
    }
    append(item);
  };

  return (
    <Flex direction='col' gap='lg' className='w-full mx-0'>
      <Flex direction='col' gap='sm' className='w-full mx-0'>
        <Flex align='center' justify='between' className='w-full mx-0'>
          <Typography
            variant='h5'
            className='text-neutral-800 font-semibold whitespace-nowrap'
          >
            Items <span className='text-error-500'>*</span>
          </Typography>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={handleAddItem}
            className='h-8 px-3 text-xs'
            buttonNamePosthog={null}
          >
            <Plus className='h-4 w-4 mr-1' />
            Add Item
          </Button>
        </Flex>
      </Flex>

      {fields.length === 0 && (
        <Flex
          direction='col'
          align='center'
          justify='center'
          className={`text-center py-6 text-sm border border-dashed rounded-md w-full mx-0 ${
            arrayLevelError
              ? 'border-error-500 bg-error-50 text-error-600'
              : 'border-neutral-300 bg-neutral-50/50 text-neutral-500'
          }`}
        >
          {arrayLevelError || 'No items added yet.'}
        </Flex>
      )}

      <AnimatePresence>
        <Flex
          direction='col'
          gap={fields.length > 0 ? 'md' : 'none'}
          className='w-full mx-0'
        >
          {fields.map((field, index) => (
            <ItemCard
              key={field.id}
              index={index}
              formMethods={formMethods}
              itemColorMap={itemColorMap}
              onRemove={() => handleRemoveItem(index)}
              commodities={commodities ?? []}
              isLoadingCommodities={isLoadingCommodities}
              hasCustomer={hasCustomer}
              hasCustomerId={hasCustomerId}
            />
          ))}
        </Flex>
      </AnimatePresence>

      {is3DVisualisationEnabled && (
        <LTLTrailerVisualizationBlock formMethods={formMethods} />
      )}
    </Flex>
  );
}
